/**
 * tiposMock.js
 *
 * Catálogo de tipos de demostración + CRUD simulado en memoria, para
 * trabajar el form de Gestión de Tipos (III.4) mientras el backend con
 * scope/has_stock/ref_prefix/select (bloque II.4.5) no esté aplicado.
 *
 * La forma de cada tipo es la que devolverá el backend tras II.4.5:
 *   {
 *     id, slug, scope, nombre, categoria, icon,
 *     has_stock, ref_prefix, ref_padding, ref_counter,
 *     fields_schema: [ { name, type, required, per_unit, options? } ],
 *     unidades            // conteo (solo lectura, lo calcula el backend)
 *   }
 *
 * El estado vive en este módulo (memoria de la pestaña). Recargar la
 * página resetea a SEED — es esperado en modo demo.
 */

let _idSeq = 100;

const SEED = [
  {
    id: 2,
    slug: 'coche',
    scope: 'interno',
    nombre: 'Coche',
    categoria: 'Vehículos',
    icon: 'car',
    has_stock: 0,
    ref_prefix: 'CH',
    ref_padding: 4,
    ref_counter: 7,
    unidades: 7,
    fields_schema: [
      { name: 'modelo', type: 'string', required: true, per_unit: false },
      { name: 'matricula', type: 'string', required: true, per_unit: true },
      { name: 'color', type: 'string', required: false, per_unit: false },
      { name: 'fecha_itv', type: 'date', required: false, per_unit: true },
      { name: 'precio', type: 'decimal', required: false, per_unit: false },
    ],
  },
  {
    id: 5,
    slug: 'movil',
    scope: 'colab',
    nombre: 'Móvil',
    categoria: 'Tecnología',
    icon: 'smartphone',
    has_stock: 1,
    ref_prefix: 'MVC',
    ref_padding: 4,
    ref_counter: 4,
    unidades: 4,
    fields_schema: [
      { name: 'modelo', type: 'string', required: true, per_unit: false },
      { name: 'imei', type: 'string', required: true, per_unit: true },
      {
        name: 'operador',
        type: 'select',
        required: false,
        per_unit: false,
        options: ['Movistar', 'Vodafone', 'Orange'],
      },
      { name: 'trabajador', type: 'user_link', required: false, per_unit: false },
    ],
  },
];

let _store = SEED.map((t) => ({ ...t, fields_schema: t.fields_schema.map((f) => ({ ...f })) }));

function clone(x) {
  return JSON.parse(JSON.stringify(x));
}

export function mockListTipos({ scope } = {}) {
  const list = scope ? _store.filter((t) => t.scope === scope) : _store;
  return clone(list);
}

export function mockGetTipo(id) {
  const t = _store.find((x) => x.id === Number(id));
  return t ? clone(t) : null;
}

export function mockSuggestPrefix(slug) {
  const clean = String(slug || '').toUpperCase().replace(/[^A-Z]/g, '');
  const consonants = clean.replace(/[AEIOU]/g, '');
  const used = new Set(_store.map((t) => t.ref_prefix));
  const candidates = [];
  if (consonants.length >= 2) candidates.push(consonants.slice(0, 2));
  for (let len = 2; len <= 5; len++) candidates.push(clean.slice(0, len));
  for (const c of candidates) {
    if (c && !used.has(c)) return c;
  }
  for (let n = 2; n < 99; n++) {
    const c = clean.slice(0, 2) + n;
    if (!used.has(c)) return c;
  }
  return 'XX';
}

export function mockCreateTipo(payload) {
  // Validación mínima espejo del backend (schemaValidator)
  if (_store.some((t) => t.slug === payload.slug && t.scope === payload.scope)) {
    const e = new Error(`Ya existe el tipo "${payload.slug}" en scope "${payload.scope}"`);
    e.statusCode = 409;
    throw e;
  }
  if (payload.ref_prefix && _store.some((t) => t.ref_prefix === payload.ref_prefix)) {
    const e = new Error(`El prefijo "${payload.ref_prefix}" ya está en uso`);
    e.statusCode = 409;
    throw e;
  }
  const created = {
    ...clone(payload),
    id: ++_idSeq,
    ref_counter: 0,
    unidades: 0,
  };
  _store = [..._store, created];
  return clone(created);
}

export function mockUpdateTipo(id, payload) {
  const idx = _store.findIndex((t) => t.id === Number(id));
  if (idx === -1) {
    const e = new Error('Tipo no encontrado');
    e.statusCode = 404;
    throw e;
  }
  const current = _store[idx];
  // ref_prefix bloqueado si ya hay unidades
  if (
    payload.ref_prefix &&
    payload.ref_prefix !== current.ref_prefix &&
    current.unidades > 0
  ) {
    const e = new Error('No se puede cambiar el prefijo: el tipo ya tiene activos');
    e.statusCode = 409;
    throw e;
  }
  // slug y scope son inmutables tras crear
  const updated = {
    ...current,
    ...clone(payload),
    id: current.id,
    slug: current.slug,
    scope: current.scope,
    ref_counter: current.ref_counter,
    unidades: current.unidades,
  };
  _store = _store.map((t, i) => (i === idx ? updated : t));
  return clone(updated);
}

export function mockDeleteTipo(id, { confirmDestructive }) {
  if (!confirmDestructive) {
    const e = new Error('Borrado destructivo: confirmar');
    e.statusCode = 400;
    throw e;
  }
  _store = _store.filter((t) => t.id !== Number(id));
  return true;
}

export function resetMockStore() {
  _store = SEED.map((t) => ({ ...t, fields_schema: t.fields_schema.map((f) => ({ ...f })) }));
}
